import {
  BlockTypes,
  MarkTypes,
  storyblokEditable,
  StoryblokRichTextNode,
} from "@storyblok/react/rsc";
import { ComponentProps, default as React, ReactElement } from "react";
import type {
  SimpleRichText as SimpleRichTextBlok,
  Image as ImageBlok,
  RichtextList as RichtextListBlok,
} from "@/generated/sb";
import { SimpleRichText as SimpleRichTextUI } from "@repo/ui-parkmobile/SimpleRichText";
import { StoryblokRichText } from "@storyblok/react";
import { hashString } from "@repo/utils/hashString";
import { LinkRichText } from "./LinkRichText";
import { ImageWithAspectRatio, RichtextList } from "@repo/ui-parkmobile";
import { imageStoryblokResolver } from "@/lib/storyblok/adaptors";

export type SimpleRichTextProps = {
  blok: SimpleRichTextBlok;
} & ComponentProps<"div">;

export function SimpleRichText({ blok, ...props }: SimpleRichTextProps) {
  if (!blok?.content) return null;

  const resolvers = {
    [MarkTypes.LINK]: (node: StoryblokRichTextNode<ReactElement>) => {
      if (!node?.text) return null;

      return <LinkRichText key={hashString(node.text)} blok={node} />;
    },
    [MarkTypes.BOLD]: (node: StoryblokRichTextNode<ReactElement>) => {
      if (!node?.text) return null;

      return (
        <strong
          key={hashString(node.text || "")}
          style={{ fontWeight: "inherit" }}
        >
          {node.text}
        </strong>
      );
    },
    [BlockTypes.HEADING]: (node: StoryblokRichTextNode<ReactElement>) => {
      if (!node?.content) return <></>;

      const level = (node.attrs?.level as number) || 3;
      const Tag = `h${level}` as keyof JSX.IntrinsicElements;

      const getHeadingClass = (lvl: number) => {
        const baseClasses =
          "uipm:text-[var(--typography-colors-text-heading)] uipm:mb-[var(--container-title-gap)]";
        switch (lvl) {
          case 1:
            return `uipm:heading-1 ${baseClasses}`;
          case 2:
            return `uipm:heading-2 ${baseClasses}`;
          case 3:
            return `uipm:heading-3 ${baseClasses}`;
          case 4:
            return `uipm:heading-4 ${baseClasses}`;
          case 5:
            return `uipm:heading-5 ${baseClasses}`;
          case 6:
            return `uipm:heading-6 ${baseClasses}`;
          default:
            return `uipm:heading-3 ${baseClasses}`;
        }
      };

      return (
        <Tag
          key={hashString(JSON.stringify(node.content))}
          className={getHeadingClass(level)}
        >
          <StoryblokRichText
            doc={
              node.content as unknown as StoryblokRichTextNode<React.ReactElement>
            }
          />
        </Tag>
      );
    },
    [BlockTypes.UL_LIST]: (node: StoryblokRichTextNode<ReactElement>) => {
      if (!node?.content) return null;

      return (
        <ul
          key={hashString(JSON.stringify(node.content))}
          className="uipm:list-disc uipm:list-outside uipm:pl-6 uipm:mb-4"
        >
          {node.content.map((item) => resolvers[BlockTypes.LIST_ITEM]?.(item))}
        </ul>
      );
    },
    [BlockTypes.OL_LIST]: (node: StoryblokRichTextNode<ReactElement>) => {
      if (!node?.content) return null;

      return (
        <ol
          key={hashString(JSON.stringify(node.content))}
          className="uipm:list-decimal uipm:list-outside uipm:pl-6 uipm:mb-4"
        >
          {node.content.map((item) => resolvers[BlockTypes.LIST_ITEM]?.(item))}
        </ol>
      );
    },
    [BlockTypes.LIST_ITEM]: (node: StoryblokRichTextNode<ReactElement>) => {
      if (!node?.content) return [<></>];

      return (
        <li
          key={hashString(JSON.stringify(node.content))}
          className="uipm:mb-2"
        >
          <StoryblokRichText
            // TODO: fix tupes and nesting
            // @ts-ignore
            resolvers={resolvers}
            doc={
              node.content as unknown as StoryblokRichTextNode<React.ReactElement>
            }
          />
        </li>
      );
    },
    [BlockTypes.COMPONENT]: (node: StoryblokRichTextNode<ReactElement>) => {
      // Handle embedded Storyblok components (like Image, RichtextList)
      if (!node?.attrs?.body) return null;

      const blokData = node.attrs.body[0] as
        | ImageBlok
        | RichtextListBlok;

      // Handle Image component
      if (blokData?.component === "image") {
        const image = imageStoryblokResolver(blokData as ImageBlok);
        if (!image) return null;

        return (
          <div key={blokData._uid} className="uipm:my-4">
            <ImageWithAspectRatio
              {...storyblokEditable(blokData as ImageBlok)}
              {...image}
            />
          </div>
        );
      }

      // Handle RichtextList component
      if (blokData?.component === "richtextList") {
        const richtextListBlok = blokData as RichtextListBlok;
        if (!richtextListBlok.content) return null;

        // Extract list items from the richtext content
        const content =
          richtextListBlok.content as StoryblokRichTextNode<React.ReactElement>;
        const listContent = content?.content?.find(
          (node) => node.type === "bullet_list" || node.type === "ordered_list",
        );

        if (!listContent?.content) return null;

        return (
          <RichtextList
            key={richtextListBlok._uid}
            columnsAmount={richtextListBlok.columnsAmount}
          >
            {listContent.content.map((item) =>
              resolvers[BlockTypes.LIST_ITEM]?.(item),
            )}
          </RichtextList>
        );
      }

      return null;
    },
  };

  return (
    <SimpleRichTextUI
      {...storyblokEditable(blok)}
      {...props}
      size={blok.size}
      fontColor={blok.fontColor}
    >
      <StoryblokRichText
        doc={blok.content as StoryblokRichTextNode<React.ReactElement>}
        // @ts-expect-error: TODO: fix types
        resolvers={resolvers}
      />
    </SimpleRichTextUI>
  );
}
